<template>
  <nav v-if="showBreadcrumb" class="relative sticky top-0 z-50 bg-background/95 backdrop-blur-md" aria-label="面包屑导航"
    role="navigation">
    <!-- 现代化背景效果 -->
    <div class="absolute inset-0">
      <!-- 渐变背景 -->
      <div class="absolute inset-0 bg-gradient-to-r from-primary/3 via-accent/5 to-primary/3"></div>
    </div>

    <div class="w-[90%] md:w-[70%] lg:w-[75%] lg:max-w-screen-xl mx-auto px-4 py-3 relative z-10">
      <ol class="flex items-center text-sm font-medium" itemscope itemtype="https://schema.org/BreadcrumbList">
        <li v-for="(crumb, index) in breadcrumbs" :key="crumb.path" class="flex items-center group"
          itemprop="itemListElement" itemscope itemtype="https://schema.org/ListItem">
          <template v-if="index < breadcrumbs.length - 1">
            <!-- 首页链接 - 现代卡片风格 -->
            <a :href="crumb.path" @click="handleNavClick(crumb.path, $event)"
              class="flex items-center gap-2.5 px-4 py-2 rounded-xl bg-card/60 border border-border/30 hover:border-primary/50 transition-all duration-300 hover:scale-[1.02] hover:shadow-md hover:bg-card/80 group"
              itemprop="item" :aria-label="`返回${crumb.name}`">
              <!-- 图标容器 -->
              <div class="relative">
                <div
                  class="absolute inset-0 bg-primary/10 rounded-lg blur-sm opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                </div>
                <svg
                  class="relative w-4 h-4 text-muted-foreground group-hover:text-primary transition-colors duration-300"
                  fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                    d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
                </svg>
              </div>
              <span itemprop="name"
                class="text-muted-foreground group-hover:text-foreground transition-colors duration-300">
                {{ crumb.name }}
              </span>
            </a>
            <meta itemprop="position" :content="(index + 1).toString()" />

            <!-- 分隔符 -->
            <div class="flex items-center mx-3">
              <svg class="w-4 h-4 text-muted-foreground/40" fill="none" stroke="currentColor" viewBox="0 0 24 24"
                aria-hidden="true">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M9 5l7 7-7 7" />
              </svg>
            </div>
          </template>
          <template v-else>
            <!-- 当前页面 - 高亮卡片 -->
            <div
              class="flex items-center gap-3 px-5 py-2.5 rounded-xl bg-gradient-to-r from-primary/10 to-accent/10 border border-primary/30 shadow-sm">
              <!-- 状态指示器 -->
              <div class="flex items-center gap-2">
                <div class="w-2 h-2 bg-green-400 rounded-full shadow-sm shadow-green-400/50"></div>
              </div>

              <!-- 页面图标 -->
              <div class="relative">
                <svg v-if="route.name === 'Home'" class="w-5 h-5 text-primary" fill="none" stroke="currentColor"
                  viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                    d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
                </svg>
                <svg v-else-if="route.name === 'DeepSeekChat'" class="w-5 h-5 text-primary" fill="none"
                  stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                    d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                </svg>
                <svg v-else class="w-5 h-5 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                    d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
              </div>

              <span class="font-semibold text-foreground" itemprop="name" :aria-current="'page'">
                {{ crumb.name }}
              </span>
            </div>
            <meta itemprop="position" :content="(index + 1).toString()" />
          </template>
        </li>
      </ol>

      <!-- 右侧装饰 - 简约状态指示器 -->
      <div class="absolute right-4 top-1/2 -translate-y-1/2 hidden md:flex items-center gap-2 opacity-40">
        <div class="text-xs text-muted-foreground">{{ breadcrumbs.length > 1 ? '导航' : '首页' }}</div>
        <div class="flex gap-1">
          <div class="w-1.5 h-1.5 bg-primary/60 rounded-full"></div>
          <div class="w-1.5 h-1.5 bg-primary/40 rounded-full"></div>
          <div class="w-1.5 h-1.5 bg-primary/20 rounded-full"></div>
        </div>
      </div>
    </div>
  </nav>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { useRoute, useRouter } from 'vue-router'

const route = useRoute()
const router = useRouter()

interface BreadcrumbItem {
  name: string
  path: string
}

// 路由名称映射
const routeNameMap: Record<string, string> = {
  'Home': '首页',
  'AboutUs': '关于我们',
  'EnergyManagement': '智能能耗管理',
  'AISolutions': 'AI智能解决方案',
  'IndustrialAutomation': '工业自动化控制',
  'IntelligentInstruments': '智能仪器仪表',
  'EnterpriseSoftware': '企业级软件定制',
  'DeepSeekChat': '创诚AI 助手'
}

// 是否显示面包屑导航 - 现在所有页面都显示
const showBreadcrumb = computed(() => {
  return route.name !== undefined
})

// 生成面包屑数据
const breadcrumbs = computed<BreadcrumbItem[]>(() => {
  const crumbs: BreadcrumbItem[] = []

  if (route.name === 'Home') {
    // 首页只显示当前页面
    crumbs.push({
      name: '创诚纪元人工智能 - 物联网+AI智能解决方案专家',
      path: '/'
    })
  } else {
    // 其他页面显示完整路径
    crumbs.push({
      name: '创诚纪元人工智能',
      path: '/'
    })

    if (route.name) {
      const currentPageName = routeNameMap[route.name as string] || route.meta?.title as string || '未知页面'
      crumbs.push({
        name: currentPageName,
        path: route.path
      })
    }
  }

  return crumbs
})

// 处理导航点击
const handleNavClick = (path: string, event?: Event) => {
  if (path === route.path) {
    event?.preventDefault()
    return
  }

  event?.preventDefault()
  router.push(path)
}
</script>

<style scoped>
/* 现代简洁面包屑导航样式 */

/* 毛玻璃效果 */
.backdrop-blur-md {
  backdrop-filter: blur(12px);
  -webkit-backdrop-filter: blur(12px);
}

/* 卡片悬停效果 */
.group:hover svg {
  filter: drop-shadow(0 2px 4px rgba(var(--primary), 0.2));
}

/* 状态指示器发光效果 */
.shadow-green-400\/50 {
  box-shadow: 0 0 6px rgba(74, 222, 128, 0.5);
}

/* 响应式调整 */
@media (max-width: 768px) {
  .container {
    padding-left: 1rem;
    padding-right: 1rem;
  }

  /* 移动端隐藏右侧装饰 */
  .hidden-md-flex {
    display: none !important;
  }

  /* 移动端调整卡片内边距 */
  .px-4 {
    padding-left: 0.75rem;
    padding-right: 0.75rem;
  }

  .px-5 {
    padding-left: 1rem;
    padding-right: 1rem;
  }

  .gap-2-5 {
    gap: 0.5rem;
  }

  .gap-3 {
    gap: 0.75rem;
  }

  /* 移动端字体调整 */
  .text-sm {
    font-size: 0.75rem;
  }
}

/* 暗色模式优化 */
@media (prefers-color-scheme: dark) {

  /* 增强暗色模式下的对比度 */
  .bg-background\/95 {
    background-color: rgba(var(--background), 0.98);
  }

  .border-border\/50 {
    border-color: rgba(var(--border), 0.6);
  }

  .bg-card\/60 {
    background-color: rgba(var(--card), 0.7);
  }

  .hover-bg-card-80:hover {
    background-color: rgba(var(--card), 0.9);
  }
}

/* 平滑过渡动画 */
* {
  transition-property: color, background-color, border-color, box-shadow, transform, opacity;
  transition-timing-function: cubic-bezier(0.4, 0, 0.2, 1);
  transition-duration: 300ms;
}

/* 增强的焦点样式 */
a:focus-visible {
  outline: 2px solid rgba(var(--primary), 0.6);
  outline-offset: 2px;
  border-radius: 0.75rem;
  box-shadow: 0 0 0 4px rgba(var(--primary), 0.1);
}

/* 微妙的悬停缩放效果 */
.hover-scale-102:hover {
  transform: scale(1.02);
}

/* 自定义滚动条（如果需要） */
::-webkit-scrollbar {
  width: 4px;
  height: 4px;
}

::-webkit-scrollbar-track {
  background: transparent;
}

::-webkit-scrollbar-thumb {
  background: rgba(var(--border), 0.3);
  border-radius: 2px;
}

::-webkit-scrollbar-thumb:hover {
  background: rgba(var(--border), 0.5);
}

/* 提升性能 */
.group {
  transform: translateZ(0);
}

/* 减少动画对于偏好设置的用户 */
@media (prefers-reduced-motion: reduce) {
  * {
    transition-duration: 0.1ms !important;
    animation-duration: 0.1ms !important;
  }
}
</style>
